import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import FeatureList from "./FeatureList";
import { Trans } from "react-i18next";
import Footer from "./Footer";
import Header from "./HeaderHome";

interface HomeProps {
  changeLanguage: (language: string) => void;
  child?: React.ReactNode;
  hideHeader?: boolean;
}

const sectionReveal = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: i * 0.15,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
    },
  }),
};

function Home({ changeLanguage, child, hideHeader = false }: HomeProps) {
  const { t } = useTranslation();
  const featureRef = useRef<HTMLElement | null>(null);

  const scrollToFeatureList = () => {
    featureRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex flex-col w-full relative bg-[#fffefd]">
      {!hideHeader && <Header changeLanguage={changeLanguage} />}

      {child}

      {/* Approach Section */}
      <section className="scroll-mt-28 relative overflow-hidden min-h-[60vh] md:min-h-screen flex flex-col justify-between px-6 md:px-20 py-20 md:py-32">
        {/* Title — top left */}
        <motion.div
          className="flex flex-col md:max-w-[60%]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
        >
          <motion.h1
            custom={0}
            variants={sectionReveal}
            className="text-4xl md:text-[4.25rem] text-left mb-0 font-gnarly-semibold leading-[1.05] tracking-tight"
          >
            <Trans i18nKey="approach_heading">
              {t("shared_living_about_people")}
            </Trans>
          </motion.h1>
        </motion.div>

        {/* Body + button — bottom right */}
        <motion.div
          className="flex flex-col items-start md:self-end md:max-w-[45%] mt-16 md:mt-0"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-15%" }}
        >
          <motion.p
            custom={1}
            variants={sectionReveal}
            className="text-base md:text-xl md:text-left font-gnarly-regular mb-8 md:mb-10 max-w-prose leading-relaxed text-neutral-500"
          >
            {t("approach_paragraph")}
          </motion.p>

          <motion.button
            custom={2}
            variants={sectionReveal}
            className="hidden md:flex self-start items-center gap-3 font-gnarly-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 hover:gap-4 bg-neutral-900 text-white hover:bg-neutral-800"
            onClick={scrollToFeatureList}
          >
            {t("approach_button")}
            <ArrowDown className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </section>

      <FeatureList scrollRef={featureRef} />

      <Footer />
    </div>
  );
}

export default Home;
